import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../utils/i18n/i18n";
import { STRING_LANGUAGE_TUTTIENGLISH } from "../../utils/const/string-const";
import {
    getWhatsAppChatUrl,
} from "../../config/public-site";
import { sharedNextPage } from "../../utils/shared-functions";

export default function LandingPage() {
    const history = useHistory();
    const { t } = useTranslation();
    const [selectedLanguage, setSelectedLanguage] = useState("en");
    const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const whatsappUrl = getWhatsAppChatUrl();

    const languages = [
        { value: "en", label: "English", flag: "🇺🇸" },
        { value: "es", label: "Español", flag: "🇪🇸" },
        { value: "pt", label: "Português", flag: "🇧🇷" },
    ];

    useEffect(() => {
        const language = localStorage.getItem(STRING_LANGUAGE_TUTTIENGLISH);
        if (language) {
            i18n.changeLanguage(language);
            setSelectedLanguage(language);
        } else {
            i18n.changeLanguage("en");
            setSelectedLanguage("en");
        }
    }, []);

    const scrollToSection = (id) => {
        setMobileNavOpen(false);
        const element = document.getElementById(id);
        if (element) {
            element.scrollIntoView({ behavior: "smooth" });
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                isLanguageDropdownOpen &&
                !event.target.closest(".language-dropdown")
            ) {
                setIsLanguageDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isLanguageDropdownOpen]);

    const LanguageDropdown = () => (
        <div className="relative language-dropdown">
            <button
                type="button"
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-indigo-50 transition border border-transparent hover:border-indigo-100"
                aria-expanded={isLanguageDropdownOpen}
                aria-haspopup="listbox"
            >
                <span className="text-xl" aria-hidden>
                    {languages.find((lang) => lang.value === selectedLanguage)?.flag}
                </span>
                <span className="text-slate-700 text-sm font-medium">
                    {languages.find((lang) => lang.value === selectedLanguage)?.label}
                </span>
                <svg
                    className={`w-4 h-4 text-slate-500 transition-transform ${isLanguageDropdownOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {isLanguageDropdownOpen && (
                <div
                    className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50"
                    role="listbox"
                >
                    {languages.map((language) => (
                        <button
                            type="button"
                            key={language.value}
                            onClick={() => {
                                setSelectedLanguage(language.value);
                                setIsLanguageDropdownOpen(false);
                                i18n.changeLanguage(language.value);
                                localStorage.setItem(STRING_LANGUAGE_TUTTIENGLISH, language.value);
                            }}
                            className={`w-full flex items-center space-x-3 px-4 py-2 hover:bg-indigo-50 transition text-left ${selectedLanguage === language.value ? "bg-indigo-50 text-indigo-800" : "text-slate-700"}`}
                            role="option"
                            aria-selected={selectedLanguage === language.value}
                        >
                            <span className="text-xl" aria-hidden>
                                {language.flag}
                            </span>
                            <span className="font-medium">{language.label}</span>
                            {selectedLanguage === language.value && (
                                <svg className="w-4 h-4 ml-auto text-indigo-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    const sectors = [
        {
            icon: "🩺",
            titleKey: "te_sector_medical_title",
            descKey: "te_sector_medical_desc",
            accent: "from-sky-500/15 to-cyan-500/10",
        },
        {
            icon: "💻",
            titleKey: "te_sector_it_title",
            descKey: "te_sector_it_desc",
            accent: "from-violet-500/15 to-indigo-500/10",
        },
        {
            icon: "🎵",
            titleKey: "te_sector_music_title",
            descKey: "te_sector_music_desc",
            accent: "from-amber-500/15 to-orange-500/10",
        },
    ];

    const steps = [
        { num: 1, titleKey: "te_step1_title", descKey: "te_step1_desc" },
        { num: 2, titleKey: "te_step2_title", descKey: "te_step2_desc" },
        { num: 3, titleKey: "te_step3_title", descKey: "te_step3_desc" },
    ];

    const accentPoints = [
        { titleKey: "te_accent_p1_title", descKey: "te_accent_p1_desc", icon: "🗣️" },
        { titleKey: "te_accent_p2_title", descKey: "te_accent_p2_desc", icon: "🎯" },
        { titleKey: "te_accent_p3_title", descKey: "te_accent_p3_desc", icon: "🌟" },
    ];

    const NavLinks = ({ className = "", onItemClick }) => (
        <nav className={className}>
            <button type="button" onClick={() => { scrollToSection("inicio"); onItemClick?.(); }} className="text-slate-600 hover:text-indigo-600 font-medium transition">
                {t("label_inicio")}
            </button>
            <button type="button" onClick={() => { scrollToSection("especialidades"); onItemClick?.(); }} className="text-slate-600 hover:text-indigo-600 font-medium transition">
                {t("te_nav_specialties")}
            </button>
            <button type="button" onClick={() => { scrollToSection("acento"); onItemClick?.(); }} className="text-slate-600 hover:text-indigo-600 font-medium transition">
                {t("te_nav_accent")}
            </button>
            <button type="button" onClick={() => { scrollToSection("pasos"); onItemClick?.(); }} className="text-slate-600 hover:text-indigo-600 font-medium transition">
                {t("te_nav_how")}
            </button>
            <button type="button" onClick={() => { scrollToSection("inscripcion"); onItemClick?.(); }} className="text-slate-600 hover:text-indigo-600 font-medium transition">
                {t("te_nav_join")}
            </button>
            <button type="button" onClick={() => { scrollToSection("contacto"); onItemClick?.(); }} className="text-slate-600 hover:text-indigo-600 font-medium transition">
                {t("label_contacto")}
            </button>
        </nav>
    );

    const currentYear = new Date().getFullYear();

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900 antialiased selection:bg-indigo-200 selection:text-indigo-950">
            <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
                <div className="absolute -top-40 -right-32 h-96 w-96 rounded-full bg-indigo-400/20 blur-3xl" />
                <div className="absolute top-1/3 -left-24 h-80 w-80 rounded-full bg-violet-400/15 blur-3xl" />
            </div>

            <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/90 backdrop-blur-md shadow-sm">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-3.5 gap-3">
                        <button
                            type="button"
                            onClick={() => scrollToSection("inicio")}
                            className="text-left font-bold text-xl sm:text-2xl tracking-tight text-indigo-900 hover:text-indigo-700 transition shrink-0"
                        >
                            {t("te_brand")}
                        </button>

                        <NavLinks className="hidden lg:flex items-center gap-7" />

                        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                            <button
                                type="button"
                                className="lg:hidden p-2 rounded-lg text-slate-700 hover:bg-slate-100 border border-slate-200"
                                onClick={() => setMobileNavOpen((o) => !o)}
                                aria-expanded={mobileNavOpen}
                                aria-label="Menu"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    {mobileNavOpen ? (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    ) : (
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    )}
                                </svg>
                            </button>
                            <div className="hidden sm:block">
                                <LanguageDropdown />
                            </div>
                            <button
                                type="button"
                                onClick={() => history.push("/login")}
                                className="bg-indigo-600 text-white px-3 sm:px-5 py-2 rounded-xl hover:bg-indigo-700 transition font-semibold text-sm shadow-md shadow-indigo-600/20 whitespace-nowrap"
                            >
                                {t("label_iniciar_sesion")}
                            </button>
                        </div>
                    </div>

                    {mobileNavOpen && (
                        <div className="lg:hidden border-t border-slate-100 py-4">
                            <NavLinks
                                className="flex flex-col gap-3 px-1"
                                onItemClick={() => setMobileNavOpen(false)}
                            />
                        </div>
                    )}

                    <div className="sm:hidden border-t border-slate-100 py-2 flex justify-center lg:hidden">
                        <LanguageDropdown />
                    </div>
                </div>
            </header>

            <main>
                <section id="inicio" className="relative px-4 sm:px-6 lg:px-8 pt-12 pb-16 md:pt-20 md:pb-24">
                    <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
                        <div className="lg:col-span-7 text-center lg:text-left">
                            <span className="inline-flex items-center gap-2 mb-5 px-4 py-1.5 rounded-full text-sm font-semibold bg-indigo-100 text-indigo-800 border border-indigo-200/60">
                                <span aria-hidden>🌍</span>
                                {t("te_hero_badge")}
                            </span>
                            <h1 className="text-4xl sm:text-5xl lg:text-[3.25rem] font-extrabold text-slate-900 leading-[1.1] tracking-tight mb-6">
                                {t("te_hero_title")}
                                <span className="block mt-1 text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-violet-600 to-indigo-600">
                                    {t("te_hero_title_highlight")}
                                </span>
                            </h1>
                            <p className="text-lg sm:text-xl text-slate-600 max-w-xl mx-auto lg:mx-0 mb-9 leading-relaxed">
                                {t("te_hero_subtitle")}
                            </p>
                            <div className="flex flex-col sm:flex-row flex-wrap justify-center lg:justify-start gap-3">
                                <div
                                    className="inline-flex justify-center items-center gap-2 bg-indigo-600 text-white px-7 py-3.5 rounded-xl text-base font-semibold hover:bg-indigo-700 transition shadow-lg shadow-indigo-600/25 cursor-pointer"
                                    onClick={() => {
                                        sharedNextPage("/register-page", {}, history);
                                    }}
                                >
                                    {t("te_hero_cta_form")}
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                </div>
                                {whatsappUrl && (
                                    <a
                                        href={whatsappUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex justify-center items-center gap-2 bg-white text-slate-800 px-7 py-3.5 rounded-xl text-base font-semibold border-2 border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/50 transition"
                                    >
                                        <WhatsAppIcon className="w-6 h-6 text-emerald-600 shrink-0" aria-hidden />
                                        {t("te_hero_cta_whatsapp")}
                                    </a>
                                )}
                                <button
                                    type="button"
                                    onClick={() => scrollToSection("especialidades")}
                                    className="inline-flex justify-center items-center gap-2 text-indigo-700 px-5 py-3.5 rounded-xl text-base font-semibold hover:bg-indigo-50 transition"
                                >
                                    {t("te_hero_secondary")}
                                    <span aria-hidden>↓</span>
                                </button>
                            </div>
                        </div>
                        <div className="lg:col-span-5 relative">
                            <div className="relative rounded-3xl border border-slate-200/80 bg-white p-6 sm:p-8 shadow-xl shadow-slate-900/5">
                                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-indigo-500/5 to-violet-500/10 pointer-events-none" />
                                <ul className="relative space-y-4 text-left text-slate-700">
                                    <li className="flex gap-3">
                                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700 font-bold text-sm">1</span>
                                        <div>
                                            <p className="font-semibold text-slate-900">{t("te_step1_title")}</p>
                                            <p className="text-sm text-slate-600 mt-0.5">{t("te_step1_desc")}</p>
                                        </div>
                                    </li>
                                    <li className="flex gap-3">
                                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700 font-bold text-sm">2</span>
                                        <div>
                                            <p className="font-semibold text-slate-900">{t("te_step2_title")}</p>
                                            <p className="text-sm text-slate-600 mt-0.5">{t("te_step2_desc")}</p>
                                        </div>
                                    </li>
                                    <li className="flex gap-3">
                                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700 font-bold text-sm">3</span>
                                        <div>
                                            <p className="font-semibold text-slate-900">{t("te_step3_title")}</p>
                                            <p className="text-sm text-slate-600 mt-0.5">{t("te_step3_desc")}</p>
                                        </div>
                                    </li>
                                </ul>
                                <div className="mt-6 pt-6 border-t border-slate-100 flex flex-wrap gap-2">
                                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">Medical</span>
                                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">IT</span>
                                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">Music</span>
                                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">Kids</span>
                                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">Adults</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section id="especialidades" className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-slate-100">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-14">
                            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-4">
                                {t("te_sectors_title")}
                            </h2>
                            <p className="text-lg text-slate-600 leading-relaxed">
                                {t("te_sectors_subtitle")}
                            </p>
                        </div>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                            {sectors.map((item) => (
                                <article
                                    key={item.titleKey}
                                    className={`group relative overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-br ${item.accent} p-7 md:p-8 shadow-sm hover:shadow-md transition-shadow`}
                                >
                                    <div className="text-4xl mb-4" aria-hidden>
                                        {item.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-2">{t(item.titleKey)}</h3>
                                    <p className="text-slate-600 leading-relaxed text-[15px] md:text-base">{t(item.descKey)}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section id="acento" className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 relative">
                    <div className="max-w-6xl mx-auto">
                        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
                            <div>
                                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight mb-4">
                                    {t("te_accent_title")}
                                </h2>
                                <p className="text-lg text-slate-600 leading-relaxed mb-8">
                                    {t("te_accent_subtitle")}
                                </p>
                                <div
                                    className="inline-flex items-center gap-2 text-indigo-700 font-semibold hover:text-indigo-900 cursor-pointer"
                                    onClick={() => {
                                        sharedNextPage("/register-page", {}, history);
                                    }}
                                >
                                    {t("te_nav_join")}
                                    <span aria-hidden>→</span>
                                </div>
                            </div>
                            <div className="space-y-4">
                                {accentPoints.map((p) => (
                                    <div
                                        key={p.titleKey}
                                        className="flex gap-4 p-5 rounded-2xl bg-white border border-slate-200 shadow-sm"
                                    >
                                        <span className="text-2xl shrink-0" aria-hidden>
                                            {p.icon}
                                        </span>
                                        <div>
                                            <h3 className="font-bold text-slate-900 mb-1">{t(p.titleKey)}</h3>
                                            <p className="text-slate-600 text-sm sm:text-base leading-relaxed">{t(p.descKey)}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                <section id="pasos" className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-indigo-950 to-slate-900 text-white">
                    <div className="max-w-6xl mx-auto">
                        <div className="text-center max-w-2xl mx-auto mb-12 md:mb-14">
                            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
                                {t("te_steps_title")}
                            </h2>
                            <p className="text-lg text-indigo-100/90">{t("te_steps_subtitle")}</p>
                        </div>
                        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                            {steps.map((step, index) => (
                                <div
                                    key={step.num}
                                    className="relative rounded-2xl bg-white/5 border border-white/10 p-7 backdrop-blur-sm"
                                >
                                    <div className="w-12 h-12 rounded-xl bg-indigo-400/30 flex items-center justify-center text-lg font-bold text-white mb-5 ring-1 ring-white/20">
                                        {step.num}
                                    </div>
                                    <h3 className="text-xl font-bold mb-2">{t(step.titleKey)}</h3>
                                    <p className="text-indigo-100/85 text-sm sm:text-base leading-relaxed">{t(step.descKey)}</p>
                                    {index < steps.length - 1 && (
                                        <div className="hidden md:block absolute top-1/2 -right-4 translate-x-1/2 -translate-y-1/2 text-indigo-300/80 text-2xl select-none" aria-hidden>
                                            →
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section id="inscripcion" className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-white">
                    <div className="max-w-3xl mx-auto text-center rounded-3xl border border-indigo-100 bg-gradient-to-br from-indigo-50/80 to-white p-8 sm:p-12 shadow-lg shadow-indigo-900/5">
                        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
                            {t("te_enroll_title")}
                        </h2>
                        <p className="text-slate-600 text-lg mb-8 leading-relaxed">
                            {t("te_enroll_subtitle")}
                        </p>
                        <div
                            className="inline-flex justify-center items-center gap-2 bg-indigo-600 text-white px-7 py-3.5 rounded-xl text-base font-semibold hover:bg-indigo-700 transition shadow-lg shadow-indigo-600/25 cursor-pointer"
                            onClick={() => {
                                sharedNextPage("/register-page", {}, history);
                            }}
                        >
                            {t("te_enroll_btn")}
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                        </div>
                        {whatsappUrl && (
                            <p className="mt-6 text-sm text-slate-500">{t("te_whatsapp_hint")}</p>
                        )}
                    </div>
                </section>
            </main>

            <footer id="contacto" className="bg-slate-950 text-slate-300 py-12 px-4 sm:px-6 lg:px-8 pb-28 sm:pb-12">
                <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8">
                    <div className="text-center md:text-left">
                        <p className="text-white font-bold text-lg">{t("te_brand")}</p>
                        <p className="text-sm text-slate-400 mt-2 max-w-md leading-relaxed">{t("te_footer_line")}</p>
                    </div>
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                        {whatsappUrl && (
                            <a
                                href={whatsappUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-500 transition"
                            >
                                <WhatsAppIcon className="w-5 h-5" />
                                WhatsApp
                            </a>
                        )}
                        <div
                            className="text-sm text-indigo-300 hover:text-indigo-200 font-medium underline-offset-2 hover:underline cursor-pointer"
                            onClick={() => {
                                sharedNextPage("/register-page", {}, history);
                            }}
                        >
                            {t("te_enroll_btn")}
                        </div>
                    </div>
                </div>
                <div className="max-w-6xl mx-auto border-t border-slate-800 mt-10 pt-8 text-center text-sm text-slate-500">
                    © {currentYear} {t("te_brand")}. {t("te_footer_rights")}
                </div>
            </footer>

            {whatsappUrl && (
                <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fixed bottom-5 right-5 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-900/30 hover:bg-emerald-400 hover:scale-105 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-emerald-600"
                    aria-label={t("te_whatsapp_fab_aria")}
                >
                    <WhatsAppIcon className="w-8 h-8" />
                </a>
            )}
        </div>
    );
}

function WhatsAppIcon({ className }) {
    return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.883 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
    );
}
